/**
 * matterRefUtils.js
 * Shared matter reference parsing — used by both BonsaiInbox and BonsaiOutlookPane.
 * Single source of truth. Do not duplicate this logic.
 */

export const MATTER_REF_PATTERNS = [
  /(?:BL\.)?[A-Z]{2,}\.[A-Z]+\.\d+\.\d+/,   // BL.VEC.002.0516
  /[A-Z]{2,}\d{4,}/,                            // VEC0031
  /\b\d{4,6}\b/,                                // 0065 (numeric refs)
];

/**
 * Parses a matter reference from an email subject line.
 * Strips common reply/forward prefixes before matching.
 * @param {string} subject
 * @returns {string|null}
 */
export function parseMatterRef(subject) {
  if (!subject) return null;
  const clean = subject.replace(/^(RE:|FW:|Fwd:|Re:|Fw:)\s*/i, '').trim();
  for (const pattern of MATTER_REF_PATTERNS) {
    const match = clean.match(pattern);
    if (match) return match[0];
  }
  return null;
}

/**
 * ownDisplayRef — a matter's OWN displayed reference, canonical-first.
 *
 * BM-817-REDUX: a legacy data fault poisoned the stored `preview.our_ref`
 * nested field on some submissions with a *linked* matter's reference, and
 * several UI sites rendered `preview.our_ref` FIRST — so the poison showed
 * on the wrong matter (BL.VEC.002.0979 displayed BL.RM.001.0517).
 *
 * The canonical `matter_reference` (top-level, then nested
 * `data.matter_reference`) is preferred; `preview.our_ref` / `our_ref` /
 * `data.Q4` are only a last-resort fallback for a genuinely un-referenced
 * draft. Mirrors backend `matter_ref_helpers.own_display_ref`.
 *
 * @param {object} submission - submission doc (may include a `preview` blob)
 * @param {object} [preview] - the separately-fetched /preview payload, if any
 * @returns {string}
 */
export function ownDisplayRef(submission, preview) {
  const sub = submission || {};
  const data = sub.data || {};
  const prev = preview || sub.preview || {};
  return (
    sub.matter_reference ||
    data.matter_reference ||
    prev.our_ref ||
    sub.our_ref ||
    data.Q4 ||
    ''
  );
}
